import { Stack, Text, XStack, YStack } from "tamagui";
import type { Product } from "@utils/store/cart/useOrder";
import { buildGasCouponDiscountMap, discountedLineTotal } from "@screen/Home/pages/GasShop/utils/couponDiscount";
import { formatOrderableGasTitle } from "@utils/func/gasTitle";
import KioskIcon from "../KioskIcon";
import { GAS_IMAGE, cylinderHeight } from "../icon/gasIcons";
import { floorIntToLabel, kiosk } from "../kioskTheme";
import { Card, SectionLabel } from "./ConfirmParts";

/**
 * 商品明細卡：逐項顯示規格、安裝資訊、數量、小計。
 * 對齊訂購頁：瓦斯統一顯示「{公斤數}KG 瓦斯」；優惠券折抵直接折在瓦斯品項上
 * （依各瓦斯品項金額比例攤、最後一筆吸收餘數），與費用明細應付總額一致。
 */
const OrderItemsCard = ({
  productList,
  count,
  couponDiscount = 0,
  hidePrice = false,
}: {
  productList: Product[];
  count: number;
  couponDiscount?: number;
  /** 瓦斯行隱藏金額或營業客戶時為 true → 不顯示各品項價格（由結帳頁傳入） */
  hidePrice?: boolean;
}) => {
  const gasCouponDiscountMap = buildGasCouponDiscountMap(productList, couponDiscount);
  // 依公斤數排序（小→大）；非瓦斯排最後。折扣以 line_id 對應，排序不影響金額。
  const sorted = [...productList].sort((a, b) => (a.kilogram ?? Infinity) - (b.kilogram ?? Infinity));
  return (
    <>
      <SectionLabel>商品（{count} 件）</SectionLabel>
      <Card>
        {sorted.map((it, idx) => {
          const name = it.gas_type ? formatOrderableGasTitle(it.kilogram) : it.display_name || "其他商品";
          const sub = [floorIntToLabel(it.floor), it.is_elevator ? "有電梯" : "無電梯", it.usage_location, it.note]
            .filter(Boolean)
            .join(" · ");
          const original = it.unit_price * it.qty;
          const shownPrice = discountedLineTotal(it, idx, gasCouponDiscountMap);
          const hasDiscount = shownPrice < original;
          /* 瓦斯用實際的桶子照片（和商品格同一組資產），高度依公斤數縮放——
             明細裡看得到 20KG 比 10KG 大一截，數量與品項才對得起來。
             非瓦斯（配件、商品）不能共用桶子圖示：那會讓人以為自己多訂了一桶瓦斯。
             照片缺漏時退回線性圖示，不留空位。
             容器 48：立方根縮放下 50KG 才是滿高，20KG 約 35px——配 16px 的品名剛好。
             設 30 的話 20KG 只剩 22px，比旁邊的字還小，看起來像縮圖而不是商品。 */
          const ROW_BOX = 48;
          const gasImg = it.gas_type ? GAS_IMAGE[it.kilogram as number] : undefined;
          return (
            <XStack key={it.line_id || idx} ai="flex-end" gap={11} py={9} borderTopWidth={idx === 0 ? 0 : 1} borderColor="#eceff4">
              <Stack width={ROW_BOX} height={ROW_BOX} ai="center" jc="flex-end" flexShrink={0}>
                {gasImg ? (
                  <img
                    src={gasImg}
                    alt=""
                    style={{ height: cylinderHeight(it.kilogram as number, ROW_BOX), width: "auto", objectFit: "contain", display: "block" }}
                  />
                ) : (
                  <KioskIcon name={it.gas_type ? "cylinder" : "cart"} size={26} color={kiosk.blue} />
                )}
              </Stack>
              <YStack flex={1} minWidth={0} alignSelf="center">
                <Text fontSize={16} fontWeight="700" color={kiosk.ink}>
                  {name}{" "}
                  <Text color={kiosk.blue} fontWeight="800">
                    ×{it.qty}
                  </Text>
                </Text>
                {sub && (
                  <Text fontSize={13} color={kiosk.grey} mt={1}>
                    {sub}
                  </Text>
                )}
              </YStack>
              {!hidePrice && (
                <YStack ai="flex-end" alignSelf="center">
                  <Text fontSize={17} fontWeight="800" color={hasDiscount ? kiosk.blue : kiosk.ink}>
                    NT${shownPrice.toLocaleString()}
                  </Text>
                  {hasDiscount && (
                    <Text fontSize={12.5} color={kiosk.grey} style={{ textDecorationLine: "line-through" }}>
                      NT${original.toLocaleString()}
                    </Text>
                  )}
                </YStack>
              )}
            </XStack>
          );
        })}
      </Card>
    </>
  );
};

export default OrderItemsCard;
